import { Fragment, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FormControl } from '@/components/ui/form';
import { Combobox, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList } from '@/components/ui/combobox';
import { OrganizationLogo } from 'src/components/Organization';
import { searchCompanies, type ICompanySearchResult } from 'src/utils/searchCompanies';
import { Card } from '@/components/ui/card';

type ISelectedVendor = {
  name: string;
  domain?: string;
  icon?: string;
};

type IVendorSearchOption = ({ kind: 'result' } & ICompanySearchResult) | { kind: 'add'; name: string };

const optionKey = (option: IVendorSearchOption) => (option.kind === 'add' ? `add-${option.name}` : option.brandId);

type VendorSearchFieldProps = {
  value: string;
  onChange: (value: string) => void;
};

const VendorSearchField = ({ value, onChange }: VendorSearchFieldProps) => {
  const [selectedVendor, setSelectedVendor] = useState<ISelectedVendor | null>(value ? { name: value } : null);
  const [vendorInputValue, setVendorInputValue] = useState('');
  const [companies, setCompanies] = useState<ICompanySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(true);

  const vendorInputRef = useRef<HTMLInputElement>(null);
  const shouldFocusVendorInputRef = useRef(false);

  useEffect(() => {
    if (shouldFocusVendorInputRef.current) {
      vendorInputRef.current?.focus();
      shouldFocusVendorInputRef.current = false;
    }
  }, [selectedVendor]);

  const debouncedSearchCompaniesRef = useRef(
    debounce(async (name: string) => {
      setIsSearching(true);
      const results = await searchCompanies(name);
      setCompanies(results);
      setIsSearching(false);
    }, 450),
  );

  const handleVendorInputValueChange = (nextValue: string) => {
    setVendorInputValue(nextValue);
    debouncedSearchCompaniesRef.current(nextValue);
  };

  const handleVendorOptionSelect = (option: IVendorSearchOption | null) => {
    if (!option) return;

    const vendor: ISelectedVendor =
      option.kind === 'result'
        ? { name: option.name, domain: option.domain, icon: option.icon }
        : { name: option.name };

    setSelectedVendor(vendor);
    onChange(vendor.name);
  };

  const handleChangeVendor = () => {
    setSelectedVendor(null);
    setVendorInputValue('');
    setCompanies([]);
    onChange('');
    shouldFocusVendorInputRef.current = true;
  };

  const vendorSearchOptions: IVendorSearchOption[] = [
    ...companies.map((company) => ({ kind: 'result' as const, ...company })),
    ...(vendorInputValue.trim() ? [{ kind: 'add' as const, name: vendorInputValue.trim() }] : []),
  ];

  if (selectedVendor) {
    return (
      <Card className="flex items-center gap-2.5 bg-muted py-2.5 px-3 shadow-none">
        <OrganizationLogo
          name={selectedVendor.name}
          logo={selectedVendor.icon}
          size="md"
          shouldFetchLogo={false}
          rounded="lg"
          classes={{
            fallback: 'text-white bg-slate-400 text-xs',
          }}
        />
        <span className="text-sm font-semibold">{selectedVendor.name}</span>
        <Button
          variant="link"
          size="sm"
          className="font-bold text-accent-software-escrow ml-auto px-0"
          onClick={handleChangeVendor}
        >
          Change
        </Button>
      </Card>
    );
  }

  return (
    <Combobox
      items={vendorSearchOptions}
      itemToStringLabel={(option: IVendorSearchOption) => option.name}
      onValueChange={handleVendorOptionSelect}
      onInputValueChange={handleVendorInputValueChange}
      open={Boolean(vendorInputValue)}
    >
      <FormControl>
        <ComboboxInput ref={vendorInputRef} placeholder="Search or enter a vendor name" showTrigger={false} />
      </FormControl>
      <ComboboxContent>
        {isSearching ? (
          <div role="status" className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
          </div>
        ) : (
          <ComboboxList>
            {(option: IVendorSearchOption) => (
              <ComboboxItem key={optionKey(option)} value={option}>
                {option.kind === 'add' ? (
                  <span className="block py-1.5 px-2">
                    Add <strong> &quot;{option.name}&quot;</strong>
                  </span>
                ) : (
                  <Fragment>
                    <OrganizationLogo name={option.name} logo={option.icon} shouldFetchLogo={false} size="sm" />
                    <div className="flex flex-col">
                      <span>{option.name}</span>
                      <span className="text-muted-foreground text-xs">{option.domain}</span>
                    </div>
                  </Fragment>
                )}
              </ComboboxItem>
            )}
          </ComboboxList>
        )}
      </ComboboxContent>
    </Combobox>
  );
};

export default VendorSearchField;
